import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { Box } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import DashboardLayout from "./components/layout/DashboardLayout";
import PrivateRoute from "./components/common/PrivateRoute";
import { AuthProvider } from "./context/AuthContext";
import { LayoutProvider } from "./context/LayoutContext";
import ErrorBoundary from "./components/common/ErrorBoundary";
import GeneratePost from "./pages/GeneratePost/GeneratePost";
import GeneratePostV2 from "./pages/GeneratePost v2/GeneratePostNew";
import GenerateImage from "./pages/GenerateImage";
import GenerateCarousel from "./pages/GenerateCarousel";
import Posts from "./pages/Posts";
import EditProfile from "./pages/EditProfile";
import Calendar from "./pages/Calendar";
import Analytics from "./pages/Analytics";
import PricingPlans from "./pages/PricingPlans";
import AdminLayout from "./layouts/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagement from "./pages/admin/UserManagement";
import TestPage from "./pages/TestPage";
import GenerationStats from "./pages/admin/GenerationStats";
import DatabaseSetup from "./pages/admin/DatabaseSetup";
import CreditManagement from "./pages/admin/CreditManagement";
import TopicGenerator from "./pages/TopicGenerator";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LinkedInCallback from "./pages/LinkedInCallback";
import TrendFinderPage from "./pages/TrendFinder";

import FabricBannerDesigner from "./components/banner-designer/FabricBannerDesigner";
import FabricImageDesigner from "./components/image-designer/FabricImageDesigner";
import FabricCarouselDesigner from "./components/carousel-generator/FabricCarouselDesigner";
import AboutSection from "./pages/AboutSection";
import { SnackbarProvider } from "notistack";
import BannerTemplates from "./pages/BannerTemplates";
import ImageTemplates from "./pages/ImageTemplates";
import CarouselTemplates from "./pages/CarouselTemplates";
import ViralLinkedInPosts from "./pages/ViralLinkedInPosts";
import CommentAssistant from "./pages/CommentAssistant";
import Influencers from "./pages/Influencers";
import GifCreatorPage from "./pages/GifCreatorPage";
import Dashboard from "./pages/Dashboard";
import EngagementProfiles from "./pages/EngagementProfiles";
import OnboardingPage from "./pages/OnboardingPage";
import TawkToChat from "./components/common/TawkToChat";
import { Toaster } from "react-hot-toast";

// Admin route guard
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const decodedToken = user?.token
    ? JSON.parse(atob(user.token.split(".")[1]))
    : null;
  console.log("AdminRoute detailed check:", {
    fullUser: user,
    token: user?.token,
    decodedToken,
    directRole: user?.role,
    tokenRole: decodedToken?.role,
  });

  if (!user || !decodedToken?.role === "admin") {
    console.log("Access denied: not an admin");
    return <Navigate to="/dashboard" />;
  }
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  const decodedToken = user?.token
    ? JSON.parse(atob(user.token.split(".")[1]))
    : null;
  console.log("AppRoutes rendering, user:", user);

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ marginTop: "20px" }}>
        <Routes>
          {/* Root redirect */}
          <Route
            path="/"
            element={
              user ? (
                decodedToken?.role === "admin" ? (
                  <Navigate to="/admin" />
                ) : (
                  <Navigate to="/dashboard/generate-post" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          {/* Public Routes */}
          <Route
            path="/login"
            element={
              !user ? (
                <Login />
              ) : decodedToken?.role === "admin" ? (
                <Navigate to="/admin" />
              ) : (
                <Navigate to="/dashboard/generate-post" />
              )
            }
          />
          <Route
            path="/signup"
            element={
              !user ? (
                <Signup />
              ) : decodedToken?.role === "admin" ? (
                <Navigate to="/admin" />
              ) : (
                <Navigate to="/dashboard/generate-post" />
              )
            }
          />
          <Route path="/pricing" element={<PricingPlans />} />

          {/* Test Route */}
          <Route path="/test" element={<TestPage />} />

          {/* Protected Routes */}
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <OnboardingPage />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/*"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to="/dashboard/home" replace />}
                    />
                    <Route path="home" element={<Dashboard />} />
                    <Route path="generate-post" element={<GeneratePost />} />
                    <Route
                      path="generate-post-v2"
                      element={<GeneratePostV2 />}
                    />
                    <Route path="generate-image" element={<GenerateImage />} />
                    <Route
                      path="generate-carousel"
                      element={<GenerateCarousel />}
                    />
                    <Route path="gif-creator" element={<GifCreatorPage />} />
                    <Route
                      path="topic-generator"
                      element={<TopicGenerator />}
                    />
                    <Route path="posts" element={<Posts />} />
                    <Route path="profile" element={<EditProfile />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route
                      path="upgrade"
                      element={
                        <Box sx={{ p: 3 }}>
                          <PricingPlans />
                        </Box>
                      }
                    />
                    <Route path="trend-finder" element={<TrendFinderPage />} />
                    <Route
                      path="viral-posts"
                      element={<ViralLinkedInPosts />}
                    />
                    <Route
                      path="banner-designer"
                      element={<FabricBannerDesigner />}
                    />
                    <Route path="about-section" element={<AboutSection />} />
                    <Route
                      path="comment-assistant"
                      element={<CommentAssistant />}
                    />
                    <Route path="influencers" element={<Influencers />} />
                    <Route
                      path="engagement-profiles"
                      element={<EngagementProfiles />}
                    />
                  </Routes>
                </DashboardLayout>
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="generations" element={<GenerationStats />} />
                    <Route path="database" element={<DatabaseSetup />} />
                    <Route path="credits" element={<CreditManagement />} />
                  </Routes>
                </AdminLayout>
              </AdminRoute>
            }
          />

          {/* Catch all redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />

          {/* Forgot Password Route */}
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Reset Password Route */}
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* LinkedIn Callback Route */}
          <Route path="/linkedin-callback" element={<LinkedInCallback />} />

          {/* Banner Templates Route */}
          <Route path="/banner-templates" element={<BannerTemplates />} />
          <Route
            path="/banner-templates/:templateId"
            element={<BannerTemplates />}
          />
          <Route
            path="/banner-designer/:templateId?"
            element={<FabricBannerDesigner />}
          />
          <Route
            path="/fabric-banner-designer/:templateId?"
            element={<FabricBannerDesigner />}
          />
          <Route
            path="/dashboard/banner-designer"
            element={<Navigate to="/banner-designer" replace />}
          />

          {/* Image Templates Route */}
          <Route path="/image-templates" element={<ImageTemplates />} />
          <Route
            path="/image-templates/:templateId"
            element={<ImageTemplates />}
          />
          <Route
            path="/image-designer/:templateId?"
            element={<FabricImageDesigner />}
          />
          <Route
            path="/dashboard/image-designer"
            element={<Navigate to="/image-designer" replace />}
          />

          {/* Carousel Templates Route */}
          <Route path="/carousel-templates" element={<CarouselTemplates />} />
          <Route
            path="/carousel-templates/:templateId"
            element={<CarouselTemplates />}
          />
          <Route
            path="/carousel-designer/:templateId?"
            element={<FabricCarouselDesigner />}
          />
          <Route
            path="/dashboard/carousel-designer"
            element={<Navigate to="/carousel-designer" replace />}
          />
        </Routes>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Toaster />
      <SnackbarProvider maxSnack={3}>
        <ErrorBoundary>
          <AuthProvider>
            <LayoutProvider>
              <Router>
                <AppRoutes />
                <TawkToChat />
              </Router>
            </LayoutProvider>
          </AuthProvider>
        </ErrorBoundary>
      </SnackbarProvider>
    </HelmetProvider>
  );
};

export default App;
